import styles from "./MonthRangePicker.module.scss";
import { AngleLeft, AngleRight, CalendarIcon } from "../../../assets/svgIcons";
import { MONTHS_OF_YEAR } from "../../../helpers";
import primaryComponents from "../../primaryComponents";
import useMonthRangePicker from "./useMonthRangePicker";

export const MonthRangePicker = ({
  startDate = null,
  endDate = null,
  onChange = () => {},
  placeholder = "Filter by date",
  classNames = "",
}) => {
  const {
    containerRef,
    isOpen,
    setIsOpen,
    displayYear,
    setDisplayYear,
    selectedStart,
    hasValue,
    triggerLabel,
    handleMonthClick,
    handleClear,
    handleSetDate,
    setHoverMonth,
    getMonthCellState,
  } = useMonthRangePicker({
    startDate,
    endDate,
    onChange,
    placeholder,
  });

  const renderMonthCell = (year, index, name, colPrefix) => {
    const state = getMonthCellState(year, index);

    const cellClasses = [
      styles.monthCell,
      state.isSingle ? styles.single : "",
      state.isStart ? styles.rangeStart : "",
      state.isEnd ? styles.rangeEnd : "",
      state.isInRange ? styles.inRange : "",
      state.isColStart ? styles.colStart : "",
      state.isColEnd ? styles.colEnd : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <button
        key={`${colPrefix}-${name}`}
        type="button"
        className={cellClasses}
        onClick={() => handleMonthClick(year, index)}
        onMouseEnter={() => setHoverMonth({ year, month: index })}
        onMouseLeave={() => setHoverMonth(null)}
      >
        {name}
      </button>
    );
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.monthRangePicker} ${classNames}`}
    >
      <div
        className={`${styles.trigger} ${isOpen ? styles.active : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className={`text-truncate mr-2 ${!hasValue ? "gray--text" : ""}`}>
          {triggerLabel}
        </span>
        <span className={styles.calendarIcon}>
          <CalendarIcon />
        </span>
      </div>

      {isOpen && (
        <div className={styles.popover}>
          <div className={styles.calendarColumns}>
            {/* Left Year Column */}
            <div className="d-flex flex-column">
              <div
                className="d-flex align-items-center justify-content-between mb-3"
                style={{ height: "36px" }}
              >
                <primaryComponents.Button
                  type="button"
                  classNames="btn btn--outline smallBtn"
                  style={{ width: "32px", height: "32px", padding: 0 }}
                  onClick={() => setDisplayYear((prev) => prev - 1)}
                  title="Previous Year"
                >
                  <AngleLeft />
                </primaryComponents.Button>
                <span
                  className="font-weight-bold dark--text text-center flex-fill"
                  style={{ fontSize: "15px" }}
                >
                  {displayYear}
                </span>
                <span style={{ width: "32px" }} />
              </div>

              <div className={styles.monthGrid}>
                {MONTHS_OF_YEAR.map((name, index) =>
                  renderMonthCell(displayYear, index, name, "left"),
                )}
              </div>
            </div>

            {/* Right Year Column */}
            <div className="d-flex flex-column">
              <div
                className="d-flex align-items-center justify-content-between mb-3"
                style={{ height: "36px" }}
              >
                <span style={{ width: "32px" }} />
                <span
                  className="font-weight-bold dark--text text-center flex-fill"
                  style={{ fontSize: "15px" }}
                >
                  {displayYear + 1}
                </span>
                <primaryComponents.Button
                  type="button"
                  classNames="btn btn--outline smallBtn"
                  style={{ width: "32px", height: "32px", padding: 0 }}
                  onClick={() => setDisplayYear((prev) => prev + 1)}
                  title="Next Year"
                >
                  <AngleRight />
                </primaryComponents.Button>
              </div>

              <div className={styles.monthGrid}>
                {MONTHS_OF_YEAR.map((name, index) =>
                  renderMonthCell(displayYear + 1, index, name, "right"),
                )}
              </div>
            </div>
          </div>

          <div
            className="d-flex align-items-center justify-content-end pt-3 border-top"
            style={{ gap: "12px" }}
          >
            <primaryComponents.Button
              type="button"
              classNames="btn btn--outline smallBtn"
              onClick={handleClear}
            >
              Clear
            </primaryComponents.Button>
            <primaryComponents.Button
              type="button"
              classNames="btn btn--primary smallBtn"
              onClick={handleSetDate}
              isDisabled={!selectedStart}
            >
              Set Date
            </primaryComponents.Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthRangePicker;
